import { createCaller } from "@dransay/api";
import { deliveryMethodKindsSchema } from "@dransay/api/models";
import type { DeliveryMethodKind } from "@dransay/db";
import type { TFunction } from "i18next";
import { useQueryState } from "nuqs";
import { useEffect, useRef } from "react";
import { data, Outlet } from "react-router";
import { useCart } from "~/features/cart/cart.context";
import i18nServer from "~/tooling/i18n/i18n.server";
import { RouteHistogram } from "~/tooling/metrics.server";
import { findVendorOption } from "~/utils/vendor";
import { getContext } from "../tooling/trpc/get-context";
import { parseAggregationGroup } from "../utils/parse-aggregation-group";
import type { Route } from "./+types/_storefront._products";
import { assertNotAborted } from "./helpers/products-common";

type VendorParams = {
	vendorId: string | null;
	deliveryMethod: DeliveryMethodKind | null;
};

async function getVendorData(
	params: VendorParams,
	caller: ReturnType<typeof createCaller>,
	t: TFunction<"product">,
) {
	if (!params.vendorId || !params.deliveryMethod) {
		return { vendor: undefined, vendorData: undefined };
	}

	const aggregationGroup = parseAggregationGroup(params.vendorId);
	const vendorsData = await caller.public.getVendors({
		filter: {
			...aggregationGroup,
			deliveryMethodKinds: params.deliveryMethod
				? [params.deliveryMethod]
				: undefined,
		},
	});

	const { vendor: vendorData } = findVendorOption(
		params.vendorId,
		params.deliveryMethod,
		t,
		vendorsData,
	);

	const vendor = vendorData
		? {
				id: params.vendorId,
				deliveryMethodKind: params.deliveryMethod,
			}
		: undefined;

	return { vendor, vendorData };
}

export { productsLayoutShouldRevalidate as shouldRevalidate } from "./helpers/products-layout-should-revalidate";

export const loader = async ({ request }: { request: Request }) => {
	const histogram = new RouteHistogram("storefront");
	assertNotAborted(request, histogram);
	const context = await getContext({ request });
	const caller = createCaller(context);
	const t = await i18nServer.getFixedT(request, "product");

	const url = new URL(request.url);
	const params: VendorParams = {
		vendorId: url.searchParams.get("vendorId"),
		deliveryMethod:
			deliveryMethodKindsSchema.safeParse(
				url.searchParams.get("deliveryMethod"),
			).data ?? null,
	};

	const { vendor, vendorData } = await getVendorData(params, caller, t);

	assertNotAborted(request, histogram);
	histogram.end(200);
	return data({ vendor, vendorData });
};

export default function ProductsLayout({ loaderData }: Route.ComponentProps) {
	const [_, setNoRedirectQuery] = useQueryState("noredirect");

	const { items } = useCart();
	const areItemsInCart = items.length > 0;

	// Skip on mount — removing a nonexistent param still triggers nuqs URL
	// update which causes React Router to revalidate all parent loaders.
	const isInitialMount = useRef(true);
	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		if (!areItemsInCart) {
			setNoRedirectQuery(null);
		}
	}, [areItemsInCart, setNoRedirectQuery]);

	return <Outlet />;
}
